/**
 * THE HUMAN SHORTLIST for one package.
 *
 * The division of labour the backend enforces, and this panel makes visible:
 *
 *   Automation PROPOSES. It can write exactly one state — PROPOSED — and it
 *   never proposes a candidate that failed a mandatory requirement.
 *   A HUMAN DECIDES. Shortlisted, preferred, excluded, removed; every one of
 *   those states a reason, and every one is attributed and dated.
 *
 * Two vocabularies live side by side here and are deliberately not merged:
 *   MEMBERSHIP  — the stored decision (the states above).
 *   LIFECYCLE   — quoted / evaluated / recommended / authorized / assigned,
 *                 derived at read time from the quote, proposal and award
 *                 records that own those facts. It is never stored on an
 *                 entry, so it cannot drift, and it is never presented as a
 *                 membership decision.
 *
 * The match evidence on an entry is frozen at the moment it was proposed and
 * the backend never refreshes it, so it is always labelled as of then.
 */
import { useState } from "react";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { SectionEmpty, UnknownValue } from "../../components/workflow/WorkflowStates";
import {
  presentShortlistEntry,
  sortShortlistEntries,
  summarizeShortlist,
  shortlistStateDisplay,
  releaseStateDisplay,
  SHORTLIST_DECISION_COPY,
  eligibilityDisplay,
} from "./exchangePresentation";
import {
  ListChecks,
  Star,
  Ban,
  Check,
  Loader2,
  IdCard,
  Info,
  AlertTriangle,
  Send,
} from "lucide-react";

const TONE_CLASS = {
  good: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  attention: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  bad: "bg-red-500/20 text-red-400 border-red-500/30",
  unknown: "bg-muted text-muted-foreground border-border",
};

const DECISION_ICON = {
  SHORTLISTED: Check,
  PREFERRED: Star,
  EXCLUDED: Ban,
  REMOVED: Ban,
};

const ShortlistRow = ({
  row,
  canDecide,
  onDecide,
  onOpenPassport,
  matchSetLoaded,
  canPrepare,
  onPrepare,
  preparing,
  releaseState,
}) => (
  <div className="rounded-lg border border-border/60 bg-muted/20 p-3 space-y-2">
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div className="min-w-0">
        {row.titleResolved ? (
          <p className="text-sm font-medium truncate">{row.title}</p>
        ) : (
          <>
            <p className="text-sm font-medium">{row.recordLabel}</p>
            {/* An entry whose listing has left the current match set still
                names a real provider record — it just cannot be titled from
                here, and guessing a name would be worse than saying so.
                When matching failed to load there IS no current match set, so
                saying the provider left it would turn a load failure into a
                statement about the provider. */}
            <p className="text-[11px] text-muted-foreground">
              {matchSetLoaded
                ? "This provider is no longer in the current match set, so its listing name is not available on this screen."
                : "Provider matching did not load, so this entry's listing name could not be resolved."}
            </p>
          </>
        )}
        <p className="text-[11px] text-muted-foreground">
          {row.recordLabel}
          {row.proposedAt ? ` · proposed ${row.proposedAt}` : ""}
        </p>
      </div>
      <Badge
        variant="outline"
        className={`${TONE_CLASS[row.tone] || TONE_CLASS.unknown} font-medium text-[10px] shrink-0`}
      >
        {row.label}
      </Badge>
    </div>

    {/* Lifecycle — read from the records that own it, not from this entry */}
    {row.lifecycle.length > 0 ? (
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
          In procurement:
        </span>
        {row.lifecycle.map((step) => (
          <Badge
            key={step.state}
            variant="outline"
            className="bg-blue-500/15 text-blue-400 border-blue-500/25 font-normal text-[10px]"
            title={`Read from ${step.evidenceCount} record(s) at request time — never stored on this shortlist entry.`}
          >
            {step.label}
          </Badge>
        ))}
      </div>
    ) : row.lifecycleJoinable ? (
      <p className="text-[11px] text-muted-foreground">
        No quote, recommendation or award is recorded against this provider on this package yet.
      </p>
    ) : (
      <p className="text-[11px] text-muted-foreground">
        This entry names an organization rather than a specific vendor record, so its
        procurement activity cannot be joined here. Open the RFQ workspace to see it.
      </p>
    )}

    {/* Evidence as it stood when the entry was proposed */}
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
      {row.proposedEligibility && (
        <span>
          When proposed:{" "}
          <span
            className={
              row.proposedEligibility === "PASS" ? "text-emerald-400" : "text-amber-400"
            }
          >
            {eligibilityDisplay(row.proposedEligibility).label}
          </span>
        </span>
      )}
      {row.proposedScore !== null && <span>fit {Math.round(row.proposedScore)}/100</span>}
      {row.proposedConfidence && <span>data confidence {row.proposedConfidence}</span>}
    </div>

    {row.decided && (
      <div className="rounded bg-background/40 border border-border/50 px-2 py-1.5 space-y-0.5">
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
          Decision recorded {row.decidedAt}
        </p>
        {row.decisionNote ? (
          <p className="text-xs">{row.decisionNote}</p>
        ) : (
          <UnknownValue reason="No reason was stored with this decision." />
        )}
      </div>
    )}

    <div className="flex flex-wrap items-center gap-2">
      {row.networkOrganizationId ? (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-[11px]"
          onClick={() => onOpenPassport?.(row.networkOrganizationId)}
        >
          <IdCard className="w-3.5 h-3.5 mr-1" />
          Passport
        </Button>
      ) : (
        <span
          className="text-[10px] text-muted-foreground inline-flex items-center gap-1"
          title="A passport needs exactly one confirmed identity anchor. This entry has none, or has more than one, so OMNΛS names no single organization."
        >
          <Info className="w-3 h-3" />
          No single confirmed identity
        </span>
      )}
      {/* PREPARE RFQ — only from a real human shortlist decision.
          PROPOSED is automation's birth state and must never masquerade as a
          human shortlist, so it does not qualify; the backend refuses it with
          shortlist_entry_not_eligible either way, and offering the control
          would be promising something it will not do. */}
      {row.preparable && canPrepare && !releaseState && (
        <Button
          size="sm"
          className="h-7 px-2 text-[11px]"
          onClick={() => onPrepare(row)}
          disabled={preparing}
        >
          {preparing ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
          ) : (
            <Send className="w-3.5 h-3.5 mr-1" />
          )}
          Prepare RFQ
        </Button>
      )}
      {releaseState && (
        <Badge
          variant="outline"
          className={`${TONE_CLASS[releaseState.tone] || TONE_CLASS.unknown} font-normal text-[10px]`}
          title="An RFQ handoff record already exists for this provider on this package. Nothing has been sent."
        >
          {releaseState.label}
        </Badge>
      )}
      {canDecide &&
        row.decisions.map((decision) => {
          const Icon = DECISION_ICON[decision] || Check;
          return (
            <Button
              key={decision}
              variant={decision === "PREFERRED" ? "outline" : "ghost"}
              size="sm"
              className="h-7 px-2 text-[11px]"
              onClick={() => onDecide(row, decision)}
            >
              <Icon className="w-3.5 h-3.5 mr-1" />
              {SHORTLIST_DECISION_COPY[decision]?.verb || decision}
            </Button>
          );
        })}
    </div>
  </div>
);

const ShortlistPanel = ({
  shortlist,
  candidatesByListing,
  matchSetLoaded,
  canPrepare,
  onPrepare,
  preparing,
  releaseStateByEntry,
  canPropose,
  canDecide,
  capabilitiesEstablished,
  proposing,
  onPropose,
  onDecide,
  onOpenPassport,
  deciding,
}) => {
  const [pending, setPending] = useState(null); // {row, decision}
  const [note, setNote] = useState("");

  const rows = sortShortlistEntries(
    (shortlist?.entries || []).map((entry) => presentShortlistEntry(entry, candidatesByListing))
  );
  const summary = summarizeShortlist(shortlist);

  // A note belongs to ONE decision. Clearing on every close stops a reason
  // written for one provider being filed against another.
  const close = () => {
    setPending(null);
    setNote("");
  };

  const submit = async () => {
    if (!pending || !note.trim()) return;
    const ok = await onDecide(pending.row, pending.decision, note.trim());
    if (ok) close();
  };

  const copy = pending ? SHORTLIST_DECISION_COPY[pending.decision] : null;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold flex items-center gap-1.5">
            <ListChecks className="w-4 h-4 text-primary" />
            Human shortlist
            {summary.count > 0 && (
              <span className="text-muted-foreground font-normal">({summary.count})</span>
            )}
          </h3>
          <p className="text-[11px] text-muted-foreground">
            OMNΛS proposes candidates. Who is actually on the list is your decision, and every
            decision is recorded with its reason.
          </p>
        </div>
        {canPropose && (
          <Button variant="outline" size="sm" onClick={onPropose} disabled={proposing}>
            {proposing && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}
            {summary.count > 0 ? "Find new candidates" : "Propose candidates"}
          </Button>
        )}
      </div>

      {summary.awaiting > 0 && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardContent className="py-2.5 px-4 text-xs">
            <span className="font-medium text-amber-400">
              {summary.awaiting} candidate{summary.awaiting === 1 ? "" : "s"} awaiting your
              decision.
            </span>{" "}
            A proposed candidate is on nobody's shortlist until a person says so.
          </CardContent>
        </Card>
      )}

      {rows.length === 0 ? (
        <SectionEmpty
          icon={ListChecks}
          title="No shortlist yet"
          hint={
            canPropose
              ? "Propose candidates to start one. Only providers that pass every mandatory requirement — or that could not be fully verified — are ever proposed."
              : "Nobody has proposed candidates for this package yet."
          }
        />
      ) : (
        <div className="space-y-2">
          {rows.map((row) => (
            <ShortlistRow
              key={row.entryId}
              row={row}
              matchSetLoaded={matchSetLoaded}
              canPrepare={canPrepare}
              onPrepare={onPrepare}
              /* A list of every entry with a preparation in flight, so one
                 finishing never clears another row's spinner. */
              preparing={(preparing || []).includes(row.entryId)}
              releaseState={
                releaseStateByEntry?.[row.entryId]
                  ? releaseStateDisplay(releaseStateByEntry[row.entryId])
                  : null
              }
              canDecide={canDecide}
              onDecide={(target, decision) => setPending({ row: target, decision })}
              onOpenPassport={onOpenPassport}
            />
          ))}
        </div>
      )}

      {/* Everything this list does not know, stated rather than implied */}
      <div className="space-y-1">
        {summary.truncated && (
          <p className="text-[11px] text-muted-foreground">
            More entries exist than were read, so the counts above are floors rather than
            totals.
          </p>
        )}
        {summary.notes.slice(1).map((note_, i) => (
          <p key={i} className="text-[11px] text-muted-foreground flex items-start gap-1">
            <AlertTriangle className="w-3 h-3 text-amber-400 mt-0.5 shrink-0" />
            {note_}
          </p>
        ))}
        {capabilitiesEstablished && !canDecide && rows.length > 0 && (
          <p className="text-[11px] text-muted-foreground">
            Deciding shortlist membership is reserved for project managers and admins.
          </p>
        )}
      </div>

      <Dialog open={!!pending} onOpenChange={(open) => !open && !deciding && close()}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {copy?.verb || "Record decision"}
              {pending?.row?.titleResolved ? ` — ${pending.row.title}` : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {pending?.decision === "EXCLUDED"
                ? "Excluding records that this provider was considered and ruled out. It deletes nothing, and a later proposal run will not quietly bring them back."
                : pending?.decision === "REMOVED"
                  ? "Removing takes this provider off the working shortlist. The entry and its history stay on the record."
                  : pending?.decision === "PREFERRED"
                    ? "Preferred marks this provider as the one you would rather use. It changes no eligibility and no ranking."
                    : "Shortlisting records that this provider is genuinely in consideration for this package."}
            </p>
            <div>
              <Label className="text-xs" htmlFor="shortlist-decision-note">
                Reason (required)
              </Label>
              <Textarea
                id="shortlist-decision-note"
                className="mt-1"
                rows={3}
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="Why — capability, capacity, price history, a conversation you had?"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Recorded against this decision and visible to your team.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={close} disabled={deciding}>
                Cancel
              </Button>
              <Button size="sm" onClick={submit} disabled={deciding || !note.trim()}>
                {deciding && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}
                {copy?.verb || "Record"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShortlistPanel;
